import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AuthorBox from './AuthorBox';
import { legacyPosts } from './legacyPosts';
import { nextStepForTags } from './postCta';

const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

// The older TSX articles get the same topic-matched closing CTA as markdown posts, from their tags.
function LegacyPostCta() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/blog\//, '').replace(/\/$/, '');
  const post = legacyPosts.find((entry) => entry.slug === slug);
  if (!post) return null;
  const nextStep = nextStepForTags(post.tags);
  return (
    <aside aria-label="Get help with this topic" data-track-location="post_cta" className="mt-14 rounded-3xl bg-[#0a1628] p-6 text-white sm:p-10">
      <h2 className="font-display text-2xl font-bold leading-tight sm:text-[1.875rem]">{nextStep.title}</h2>
      <p className="mt-2.5 max-w-2xl leading-relaxed text-slate-300">{nextStep.body}</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-white px-[22px] font-semibold text-[#0a1628] hover:bg-slate-100">{nextStep.booking}</a>
        {nextStep.links.map((link) => (
          <Link key={link.href} to={link.href} className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-white/30 px-[22px] text-center font-semibold text-white hover:bg-white/10">{link.label}</Link>
        ))}
      </div>
    </aside>
  );
}

interface BlogLayoutProps {
  children: React.ReactNode;
  /** Index pages: a wider column. */
  wide?: boolean;
  /** Markdown post template: the page lays out its own full-width hero and columns. */
  template?: boolean;
}

export default function BlogLayout({ children, wide = false, template = false }: BlogLayoutProps) {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {template ? (
        <main className="pb-16">{children}</main>
      ) : (
        <main className={`${wide ? 'max-w-7xl' : 'max-w-5xl'} mx-auto px-5 pb-16 pt-28 sm:px-6 sm:pt-32 lg:px-8`}>
          {children}
          {!wide && <LegacyPostCta />}
          {!wide && <AuthorBox className="mt-14" />}
        </main>
      )}

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <img
                src="/images/linus-tse-linkedin-photo.jpeg"
                alt="Linus Tse"
                className="h-14 w-14 rounded-full object-cover ring-4 ring-white"
              />
              <div>
                <p className="font-semibold text-slate-950">Written from the work</p>
                <p className="mt-1 max-w-xl text-sm text-slate-600">Practical notes from Linus Tse on building, fixing, and operating modern data systems.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-0 text-sm font-semibold text-[#1a4e94] sm:gap-y-2 [&>a]:inline-flex [&>a]:min-h-[40px] [&>a]:items-center sm:[&>a]:min-h-0">
              <a href="mailto:linus@greyfielddata.com" className="hover:text-slate-950">Email Linus</a>
              <a href="https://www.linkedin.com/in/linustse/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-950">LinkedIn</a>
              <a href="https://linustse.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-950">linustse.com</a>
            </div>
          </div>
          <nav aria-label="Data consulting services" className="mt-6 flex flex-wrap gap-x-5 gap-y-0 border-t border-slate-200 pt-6 text-sm font-semibold text-[#1a4e94] sm:gap-y-2 [&>a]:inline-flex [&>a]:min-h-[40px] [&>a]:items-center sm:[&>a]:min-h-0">
            <Link to="/services/snowflake-consulting" className="hover:text-slate-950">Snowflake consulting</Link>
            <Link to="/services/sigma-consulting" className="hover:text-slate-950">Sigma consulting</Link>
            <Link to="/ecommerce" className="hover:text-slate-950">Ecommerce data warehouses</Link>
            <Link to="/case-studies" className="hover:text-slate-950">Client case studies</Link>
            <Link to="/compare" className="hover:text-slate-950">Tool comparisons</Link>
            <Link to="/resources" className="hover:text-slate-950">Guides and tools</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
